import { useQuery } from '@tanstack/react-query'
import { fetchAirQuality, fetchOpenMeteoCurrent } from '@/lib/openmeteo'
import { fetchSpcRisk, type SpcRisk } from '@/lib/spc'
import { getPressureSeries, pressureTrend, type PressureTrend } from '@/lib/nws/client'
import { tempF, mToMi } from '@/lib/nws/units'
import type { NwsPoint, NwsObservation } from '@/lib/nws/types'

export interface ExtraConditions {
  aqi: number | null
  uv: number | null
  dewPointF: number | null
  visibilityMi: number | null
  spc: SpcRisk | null
  pressureTrend: PressureTrend
}

/**
 * Composes the new keyless data sources (Open-Meteo AQI + UV, SPC local risk) and the
 * NWS pressure-trend series, and merges dew point + visibility from the NWS station
 * observation first, falling back to Open-Meteo when the station value is null.
 */
export function useExtraConditions(
  lat: number | null,
  lon: number | null,
  point: NwsPoint | undefined,
  observation: NwsObservation | null,
): ExtraConditions {
  const enabled = lat != null && lon != null

  const aq = useQuery({
    queryKey: ['aqi', lat, lon],
    queryFn: () => fetchAirQuality(lat!, lon!),
    enabled,
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    retry: 1,
  })
  const om = useQuery({
    queryKey: ['openmeteo-current', lat, lon],
    queryFn: () => fetchOpenMeteoCurrent(lat!, lon!),
    enabled,
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    retry: 1,
  })
  const spc = useQuery({
    queryKey: ['spc', lat == null ? null : lat.toFixed(1), lon == null ? null : lon.toFixed(1)],
    queryFn: () => fetchSpcRisk(lat!, lon!),
    enabled,
    staleTime: 15 * 60_000,
    refetchInterval: 30 * 60_000,
    retry: 1,
  })
  const series = useQuery({
    queryKey: ['pressure-series', point?.gridId, point?.gridX, point?.gridY],
    queryFn: () => getPressureSeries(point!),
    enabled: !!point,
    staleTime: 10 * 60_000,
    refetchInterval: 10 * 60_000,
    retry: 1,
  })

  // Dew point + visibility: NWS station first, Open-Meteo fallback.
  const dewPointF = tempF(observation?.dewpoint) ?? om.data?.dewPointF ?? null
  const visMi =
    observation?.visibility?.value != null ? mToMi(observation.visibility.value) : null
  const visibilityMi = visMi ?? om.data?.visibilityMi ?? null

  return {
    aqi: aq.data?.usAqi ?? null,
    uv: om.data?.uvIndex ?? null,
    dewPointF,
    visibilityMi,
    spc: spc.data ?? null,
    pressureTrend: pressureTrend(series.data ?? []),
  }
}
